import React, { type FormEvent, Suspense, useEffect, useState } from 'react';
import { CreativeTypes, ElementUpdateTypes, MultiPageVideoModel, type TextElement } from '@bynder-studio/render-core';
import { Divider, Form } from '@bynder/design-system';
import { useTranslate } from '@bynder/localization';
import useAccessRights from 'packages/hooks/useAccessRights';
import useEditor from '~/hooks/useEditor';
import useEditorSelectedElement from '~/hooks/useEditorSelectedElement';
import useEditorSelectedElementContentTransform from '~/hooks/useEditorSelectedElementContentTransform';
import useForceUpdate from '~/hooks/useForceUpdate';
import TextBackground from 'packages/pages/editor/RightSideMenu/Shots/Text/TextBackground';
import { TextDirection } from 'packages/pages/editor/RightSideMenu/Shots/Text/TextDirection';
import { useEffectivelyLocked } from 'packages/hooks/useEffectivelyLocked';
import features from '~/configs/features';
import SettingsTabs from '../Tabs';
import ElementAlignment from '../ElementAlignment';
import Width from '../Size/Width';
import LockToggler from '../Size/LockToggle';
import Height from '../Size/Height';
import VerticalPosition from '../Position/Vertical';
import HorizontalPosition from '../Position/Horizontal';
import Rotation from '../Rotation';
import Opacity from '../Opacity';
import Fonts from './Fonts';
import LineHeight from './LineHeight';
import LetterSpacing from './LetterSpacing';
import HorizontalAlignment from './TextAlignment/Horizontal';
import VerticalAlignment from './TextAlignment/Vertical';
import FontColor from './Fonts/FontColor';
import TextStyles from './TextStyles';
import TextWrap from './TextWrap';
import Masking from '../Masking';
import Shadow from '../Shadow';
import Duration from '../Duration';
import Animations from '../../Animations';
import { BlendMode } from '../BlendMode';
import AllowToggleVisibility from '../AllowToggleVisibility';
import ContentProperty from '../ContentProperty';
import { TextTransform } from './TextTransform/TextTransform';
import FontScale from './TextScale/FontScale';
import ParagraphSpacing from './ParagraphSpacing';
import MoreTextOptions from './MoreTextOptions';
import Outline from './Outline';
import OutlineModes from './Outline/OutlineModes';
import Script from './Script';
import Decoration from './Decoration';
import { SizeFormRow } from '../index.styled';
import { FormSectionContent, InspectorContent, InspectorHeader, Section } from '../../index.styled';
import { FormRow2to1 } from './index.styled';
import useEditorSidebarShortcuts from 'packages/hooks/shortcuts/useEditorSidebarShortcuts';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import MaybeTooltip from 'packages/common/MaybeTooltip';

const Text = () => {
    const forceUpdate = useForceUpdate();
    const { translate } = useTranslate();
    const { isPlatformAllowed } = useAccessRights();
    const { creativeModel, creativeType, manipulationRenderer, frameRate } = useEditor();
    const { selectedElement, selectedElements } = useEditorSelectedElement<TextElement>();
    const { contentTransform } = useEditorSelectedElementContentTransform();
    const isLockedByParent = useEffectivelyLocked();
    const [moreOptions, setMoreOptions] = useState(false);

    const tabs = [
        { value: 'text', label: translate('editor.sidebar.shots.text.tabs.text') },
        { value: 'text_animation', label: translate('editor.sidebar.shots.animation.tabs.text') },
    ];

    const [activeTab, setTab] = useState(tabs[0].value);

    useEffect(() => {
        if (!creativeModel) {
            return;
        }

        return creativeModel.onElementsPartialUpdate(ElementUpdateTypes.LOCK, forceUpdate);
    }, [creativeModel, forceUpdate]);

    const defaultProps = {
        creativeModel,
        selectedElement,
        disabled: isLockedByParent,
    };

    useEditorSidebarShortcuts({
        setAnimationsTab: () => {
            if (creativeType === 'VIDEO') {
                setTab(tabs[1].value);
            }
        },
    });

    return (
        <>
            {creativeType === 'VIDEO' && (
                <InspectorHeader aria-labelledby="shots-text-tabs-settings">
                    <SettingsTabs tabs={tabs} activeTab={activeTab} onChange={(value) => setTab(value)} />
                </InspectorHeader>
            )}
            <InspectorContent withTabs={creativeType === 'VIDEO'}>
                {activeTab === tabs[0].value && (
                    <>
                        <Section aria-labelledby="shots-alignment-settings">
                            <ElementAlignment creativeModel={creativeModel} selectedElements={selectedElements} />
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-size-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <SizeFormRow>
                                    <Width creativeType={creativeType} {...defaultProps} />
                                    <LockToggler selectedElement={selectedElement} forceUpdate={forceUpdate} />
                                    <Height creativeType={creativeType} {...defaultProps} />
                                </SizeFormRow>
                                <Form.Row>
                                    <HorizontalPosition {...defaultProps} />
                                    <VerticalPosition {...defaultProps} />
                                </Form.Row>
                                <Form.Row>
                                    <Rotation {...defaultProps} />
                                    <Opacity {...defaultProps} />
                                </Form.Row>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        {isPlatformAllowed([features.CONTENT_PROPERTIES]) && (
                            <>
                                <Section aria-labelledby="shots-text-content-property">
                                    <Suspense>
                                        <ContentProperty
                                            creativeModel={creativeModel}
                                            selectedElements={selectedElements}
                                            disabled={defaultProps.disabled}
                                        />
                                    </Suspense>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shots-text-font-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.sidebar.shots.textarea.label')}</Form.Label>
                                    <Suspense>
                                        <TextStyles {...defaultProps} />
                                    </Suspense>
                                    <Fonts {...defaultProps} />
                                    <Form.Row>
                                        <LineHeight {...defaultProps} />
                                        <LetterSpacing {...defaultProps} />
                                    </Form.Row>
                                    <Form.Row>
                                        <ParagraphSpacing {...defaultProps} />
                                        <MoreTextOptions open={moreOptions} onChange={setMoreOptions} />
                                    </Form.Row>
                                    {moreOptions && (
                                        <>
                                            <Form.Row>
                                                <Decoration {...defaultProps} />
                                                <Script {...defaultProps} />
                                            </Form.Row>
                                            <FormRow2to1>
                                                <TextTransform {...defaultProps} withLabel />
                                                <Outline {...defaultProps} />
                                            </FormRow2to1>
                                            <OutlineModes {...defaultProps} />
                                        </>
                                    )}
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-text-font-color">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <FontColor {...defaultProps} />
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-text-alignment">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.sidebar.shots.text.alignment.title')}</Form.Label>
                                    <Form.Row>
                                        <HorizontalAlignment {...defaultProps} contentTransform={contentTransform} />
                                    </Form.Row>
                                    <Form.Row>
                                        <VerticalAlignment {...defaultProps} contentTransform={contentTransform} />
                                    </Form.Row>
                                    <Form.Row>
                                        <TextDirection {...defaultProps} />
                                    </Form.Row>
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Section aria-labelledby="shots-text-controls-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <TextWrap {...defaultProps} />
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-text-font-scale">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <FontScale {...defaultProps} />
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        {creativeType === CreativeTypes.VIDEO && (
                            <>
                                <Section aria-labelledby="shots-text-duration">
                                    <Form onSubmit={preventSubmit}>
                                        <Duration
                                            {...defaultProps}
                                            creativeModel={creativeModel as MultiPageVideoModel}
                                            frameRate={frameRate}
                                        />
                                    </Form>
                                </Section>
                                <Divider />
                            </>
                        )}
                        <Section aria-labelledby="shots-image-visibility">
                            <Form onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.visibility_control')}</Form.Label>
                                    <MaybeTooltip
                                        content={`${translate(
                                            'editor.element.visibility_control.tooltip',
                                        )} (${parseShortcutForTooltip(['s'])})`}
                                        hasTooltip={!defaultProps.disabled}
                                    >
                                        <AllowToggleVisibility
                                            creativeModel={creativeModel}
                                            selectedElements={[selectedElement]}
                                            disabled={defaultProps.disabled}
                                        />
                                    </MaybeTooltip>
                                </Form.Group>
                            </Form>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-text-masking-settings">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.element.mask')}</Form.Label>
                                    <Masking
                                        {...defaultProps}
                                        manipulationRenderer={manipulationRenderer}
                                        creativeType={creativeType}
                                    />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                        <Divider />
                        <Section aria-labelledby="shots-text-shadow">
                            <FormSectionContent onSubmit={preventSubmit}>
                                <Form.Group>
                                    <Form.Label>{translate('editor.sidebar.shots.text.shadow.label')}</Form.Label>
                                    <Shadow {...defaultProps} />
                                </Form.Group>
                                <Divider />
                                <>
                                    <Form.Group>
                                        <TextBackground {...defaultProps} />
                                    </Form.Group>
                                    <Divider />
                                </>
                                <Form.Group>
                                    <BlendMode disabled={defaultProps.disabled} />
                                </Form.Group>
                            </FormSectionContent>
                        </Section>
                    </>
                )}
                {activeTab === tabs[1].value && <Animations element={selectedElement} disabled={isLockedByParent} />}
            </InspectorContent>
        </>
    );
};

export default Text;

function preventSubmit(e: FormEvent) {
    e.preventDefault();
}
